import { Paper } from '@mantine/core';
import {
  ChatLoadError,
  ChatLoadMoreTrigger,
  ChatScrollArea,
} from '@packages/chat/ui';
import { useCallback, type Ref } from 'react';
import type { CandidateInterviewChat } from '../hooks/useCandidateInterviewChat';
import { useListenVoiceMessage } from '../hooks/useListenCandidateVoiceMessage';
import { CandidateInterviewChatMessage } from './CandidateInterviewChatMessage';
import classes from './candidateInterviewChatMessageList.module.scss';

const RETRY_LABEL = 'Повторить';

type CandidateInterviewChatMessageListProps = {
  chat: Pick<CandidateInterviewChat, 'items' | 'history' | 'scrollToLatestKey'>;
  /** Tracks whether the chat is on screen; only a visible chat is polled. */
  viewportRef: Ref<HTMLDivElement>;
};

export const CandidateInterviewChatMessageList = ({
  chat: { items, history, scrollToLatestKey },
  viewportRef,
}: CandidateInterviewChatMessageListProps) => {
  const { openCanditateVoiceMessage } = useListenVoiceMessage();
  const { initialLoad, history: olderHistory } = history;

  const handleOpenModal = useCallback(
    (audioUrl: string, fileName: string) => {
      openCanditateVoiceMessage({
        type: 'listen',
        audioUrl,
        fileName,
        // The chat owns the audio URLs of its messages.
        onClose: () => undefined,
      });
    },
    [openCanditateVoiceMessage]
  );

  return (
    <Paper
      ref={viewportRef}
      bg='primary.0'
      px={{ base: 8, sm: 16, md: 24 }}
      py={{ base: 8, sm: 16, md: 24 }}
    >
      <ChatScrollArea
        gap={24}
        mah={524}
        h='100%'
        className={classes.wrapper}
        loadMore={{
          hasMore: olderHistory.hasMore,
          isLoading: olderHistory.isFetching,
          hasError: Boolean(olderHistory.error),
          onLoadMore: olderHistory.loadMore,
        }}
        scrollToLatestKey={scrollToLatestKey}
      >
        {items.map(({ key, ...item }) => (
          <CandidateInterviewChatMessage
            key={key}
            {...item}
            handleOpenModal={handleOpenModal}
          />
        ))}
        <ChatLoadMoreTrigger />
        {olderHistory.error && (
          <ChatLoadError
            message='Не удалось загрузить предыдущие сообщения'
            retryLabel={RETRY_LABEL}
            isRetrying={olderHistory.isFetching}
            onRetry={() => void olderHistory.loadMore()}
          />
        )}
        {initialLoad.error && (
          <ChatLoadError
            message='Не удалось загрузить переписку'
            retryLabel={RETRY_LABEL}
            isRetrying={initialLoad.isFetching}
            onRetry={() => void initialLoad.retry()}
          />
        )}
      </ChatScrollArea>
    </Paper>
  );
};
